/**
 * A11 / §5.5 — pincode → estimated delivery window.
 *
 * Static zone table, no courier API. See blocks/delivery-estimate.liquid for why, and for
 * the swap path to Delhivery's expected_tat endpoint (replace `lookupZone`, nothing else).
 *
 * The pure functions are exported so knowledge-base/scripts/test-delivery-estimate.mjs can
 * assert on them without a browser.
 */

const MS_DAY = 86400000;

/**
 * The zone table lives in ONE place — assets/delivery-zones.csv — so every product
 * template, and later every other surface that needs a delivery promise, reads the same
 * numbers. Editing it does not require touching any block's settings.
 *
 * Columns: zone,prefixes,min_days,max_days,cod
 * `prefixes` is a space-separated list of pincode prefixes of any length, or `*` for the
 * catch-all. Standard CSV quoting applies, so a zone name may contain commas.
 *
 * A malformed row is dropped whole rather than half-read — a dropped zone falls through
 * to the catch-all, a half-read one would quote a wrong date.
 */
export function parseCsvRow(line) {
  const cells = [];
  let cell = '';
  let quoted = false;
  for (let i = 0; i < line.length; i += 1) {
    const ch = line[i];
    if (quoted) {
      if (ch === '"') {
        if (line[i + 1] === '"') {
          cell += '"';
          i += 1;
        } else {
          quoted = false;
        }
      } else {
        cell += ch;
      }
    } else if (ch === '"') {
      quoted = true;
    } else if (ch === ',') {
      cells.push(cell.trim());
      cell = '';
    } else {
      cell += ch;
    }
  }
  cells.push(cell.trim());
  return cells;
}

export function parseZones(raw) {
  const lines = String(raw || '')
    .split(/\r?\n/)
    .map((line) => line.trim())
    .filter((line) => line && !line.startsWith('#'));

  // Drop the header row if present, so the file stays self-documenting.
  if (lines.length && /^zone\s*,/i.test(lines[0])) lines.shift();

  return lines
    .map((line) => {
      const [name, prefixRaw, min, max, cod] = parseCsvRow(line);
      if (!name || !prefixRaw) return null;
      const minDays = parseInt(min, 10);
      const maxDays = parseInt(max, 10);
      if (!Number.isFinite(minDays) || !Number.isFinite(maxDays)) return null;
      if (minDays < 0 || maxDays < minDays) return null;
      return {
        name,
        prefixes: prefixRaw.split(/[\s,]+/).filter(Boolean),
        minDays,
        maxDays,
        cod: cod === undefined || cod === '' ? null : /^(y|yes|true|1)$/i.test(cod),
      };
    })
    .filter(Boolean);
}

/**
 * One fetch per URL for the whole page, shared across however many blocks are mounted.
 * The CSV is a static CDN asset, so it caches hard and costs nothing after the first hit.
 */
const zoneCache = new Map();

export function loadZones(url, fetchImpl = fetch) {
  if (!zoneCache.has(url)) {
    zoneCache.set(
      url,
      fetchImpl(url, { cache: 'force-cache' })
        .then((res) => {
          if (!res.ok) throw new Error(`zone table ${res.status}`);
          return res.text();
        })
        .then(parseZones)
        .catch(() => null)
    );
  }
  return zoneCache.get(url);
}

/**
 * Longest matching prefix wins, so the table can be maintained in whatever order reads
 * best rather than most-specific-first. `*` is the catch-all and only applies when
 * nothing else matched.
 *
 * Swap this for a fetch to the Delhivery proxy when it exists — same return shape.
 */
export function lookupZone(zones, pincode) {
  let best = null;
  let bestLen = -1;
  for (const zone of zones) {
    for (const prefix of zone.prefixes) {
      if (prefix === '*') {
        if (bestLen < 0) {
          best = zone;
          bestLen = 0;
        }
      } else if (pincode.startsWith(prefix) && prefix.length > bestLen) {
        best = zone;
        bestLen = prefix.length;
      }
    }
  }
  return best;
}

/** The courier does not deliver on Sundays; roll forward rather than under-promise. */
export function addWorkingDays(from, days) {
  const date = new Date(from.getTime());
  let remaining = days;
  while (remaining > 0) {
    date.setTime(date.getTime() + MS_DAY);
    if (date.getDay() !== 0) remaining -= 1;
  }
  return date;
}

export function isValidPincode(value) {
  return /^[1-9][0-9]{5}$/.test(value);
}

/**
 * Intl formats ranges correctly per locale — "Aug 1–3" in en-US, "1–3 Aug" in en-IN.
 * A hand-rolled "start + dash + end day" gets day-first locales wrong, which is most of
 * the audience here. Falls back to two full dates on the few engines without formatRange.
 */
export function formatRange(earliest, latest, fmt) {
  if (earliest.getTime() === latest.getTime()) return fmt.format(earliest);
  if (typeof fmt.formatRange === 'function') return fmt.formatRange(earliest, latest);
  return `${fmt.format(earliest)} \u2013 ${fmt.format(latest)}`;
}

if (typeof customElements !== 'undefined' && !customElements.get('delivery-estimate')) {
  const fmt = new Intl.DateTimeFormat(undefined, { month: 'short', day: 'numeric' });

  class DeliveryEstimate extends HTMLElement {
    connectedCallback() {
      this.form = this.querySelector('[data-ref="form"]');
      this.input = this.querySelector('[data-ref="input"]');
      this.result = this.querySelector('[data-ref="result"]');
      this.pincodeOut = this.querySelector('[data-ref="pincode"]');
      this.note = this.querySelector('[data-ref="note"]');
      this.error = this.querySelector('[data-ref="error"]');
      this.dates = {
        ordered: this.querySelector('[data-ref="date-ordered"]'),
        dispatched: this.querySelector('[data-ref="date-dispatched"]'),
        delivered: this.querySelector('[data-ref="date-delivered"]'),
      };

      this.zonesPromise = loadZones(this.dataset.zonesUrl);

      this.form.addEventListener('submit', (event) => {
        event.preventDefault();
        this.check(this.input.value.trim());
      });

      // Re-typing the same pincode on every product is pure friction.
      try {
        const saved = localStorage.getItem('accufit:pincode');
        if (saved) {
          this.input.value = saved;
          this.check(saved, true);
        }
      } catch {
        /* private mode or blocked storage — the form still works */
      }
    }

    async check(pincode, silent = false) {
      if (!isValidPincode(pincode)) {
        this.fail(silent ? '' : 'Enter a valid 6-digit pincode.');
        return;
      }

      const zones = await this.zonesPromise;
      if (!zones) {
        // The table failed to load. Say nothing rather than guess a date.
        this.fail(silent ? '' : 'Delivery estimates are unavailable right now.');
        return;
      }

      const zone = lookupZone(zones, pincode);
      if (!zone) {
        this.fail('We do not deliver to this pincode yet.');
        return;
      }

      try {
        localStorage.setItem('accufit:pincode', pincode);
      } catch {
        /* ignore */
      }

      const dispatch = parseInt(this.dataset.dispatchDays, 10) || 0;
      const now = new Date();

      this.dates.ordered.textContent = fmt.format(now);
      this.dates.dispatched.textContent = fmt.format(addWorkingDays(now, dispatch));
      this.dates.delivered.textContent = formatRange(
        addWorkingDays(now, dispatch + zone.minDays),
        addWorkingDays(now, dispatch + zone.maxDays),
        fmt
      );

      const parts = [];
      if (zone.cod === true) parts.push('Cash on delivery available');
      if (zone.cod === false) parts.push('Prepaid only');
      if (this.dataset.cutoffNote) parts.push(this.dataset.cutoffNote);
      this.note.textContent = parts.join(' \u00b7 ');

      this.pincodeOut.textContent = pincode;
      this.error.textContent = '';
      this.result.hidden = false;
    }

    fail(message) {
      this.result.hidden = true;
      this.note.textContent = '';
      this.error.textContent = message;
    }
  }

  customElements.define('delivery-estimate', DeliveryEstimate);
}
